import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198
    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        countryName,
        currencyCd,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings
    const standardServicing = props.standardServicing
    const currencyCd = props.currencyCd
    const requestType = 'Aircon Services'
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const countryName = props.countryName

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

  return (
    <>
      <Head>
        <title>Faulty Aircon Thermostat Replacement & Repair Cost</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Aircon Thermostat installation can cost anywhere between $100 to $350 based on the type, brand and quality of the capacitor, and the labor charges of the aircon technician."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Faulty Aircon Thermostat Replacement & Repair Cost`}/>
        <meta property="og:description" content="Aircon Thermostat installation can cost anywhere between $100 to $350 based on the type, brand and quality of the capacitor, and the labor charges of the aircon technician." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-18T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon/aircon-thermostat.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon/aircon-thermostat.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat"/>
        <meta name="twitter:title" content={`Faulty Aircon Thermostat Replacement & Repair Cost`}/> 
        <meta name="twitter:description" content="Aircon Thermostat installation can cost anywhere between $100 to $350 based on the type, brand and quality of the capacitor, and the labor charges of the aircon technician."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat"},"headline":"Faulty Aircon Thermostat Replacement & Repair Cost","description":"Aircon Thermostat installation can cost anywhere between $100 to $350 based on the type, brand and quality of the capacitor, and the labor charges of the aircon technician.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/aircon/aircon-thermostat.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-18T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat","name":"Aircon Thermostat","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-thermostat"}}]}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="aircon-thermostat" className='relative'>
                    <AirconRepair 
                        setRequestForm={setRequestForm} 
                        setIsQuickBook={setIsQuickBook} 
                        title={"Faulty Aircon Thermostat Replacement & Repair Cost"} 
                        problem={"Install Aircon Thermostat"}
                        heroImage="blog/living/aircon/aircon-thermostat-repair.jpg"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm && 
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='max-w-5xl mx-auto py-10 px-2 leading-8'>
                <div className='py-2 space-y-5'>
                    <header className='space-y-2'>
                    <h2 className='font-bold text-xl ms:text-2xl uppercase'>What is an Aircon Thermostat?</h2>
                    <p>
                        An aircon thermostat is a device that regulates and maintains indoor temperatures by controlling the air conditioning system. Found in most modern residential, commercial, and industrial aircon setups, it ensures consistent comfort based on your desired settings.
                    </p>
                    </header>

                    <section className='space-y-2'>
                    <h3 className='font-bold text-2xl'>How Does an Aircon Thermostat Work?</h3>
                    <p>
                        The thermostat continuously senses the ambient room temperature and compares it to the target temperature set by the user. If the room becomes warmer or cooler than the set point, the thermostat signals the aircon system to turn on or off, adjusting the output until the desired temperature is reached.
                    </p>
                    <p>
                        Once the correct temperature is achieved, the thermostat stops the cooling or heating process, resuming operation as necessary to maintain comfort.
                    </p>
                    <p>
                        Modern thermostats often include programmable schedules so you can customize temperatures based on time of day or day of the week—helping save energy when the space is unoccupied.
                    </p>
                    <p>
                        Some advanced models are smart-enabled, allowing remote control via smartphone apps or smart home systems, giving you flexible control even when you&apos;re away.
                    </p>
                    </section>

                    <div className='relative h-96'>
                    <Image
                        className='rounded-lg'
                        src={s3Static + "blog/living/aircon/aircon-thermostat-repair.jpg"}
                        fill
                        style={{ objectFit: 'cover' }}
                        placeholder='blur'
                        blurDataURL={s3Static + "placeholder.png"}
                        alt='aircon thermostat connects to mobile'
                    />
                    </div>

                    <section className='py-2 space-y-2'>
                    <h2 className='text-xl font-bold uppercase'>Signs of a Faulty Aircon Thermostat</h2>
                    <p>
                        A malfunctioning thermostat can cause inconsistent temperatures, higher energy usage, or even damage to your aircon system. Watch for these warning signs:
                    </p>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>1. Inaccurate Temperature Readings</h3>
                        <p>
                        If your room feels much warmer or cooler than the thermostat displays, the sensor may be faulty—causing the aircon to run unnecessarily or not at all.
                        </p>
                    </div>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>2. Inability to Reach Set Temperature</h3>
                        <p>
                        When the aircon fails to hit the target temperature, your thermostat might not be communicating properly with the cooling system.
                        </p>
                    </div>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>3. Frequent On-Off Cycling</h3>
                        <p>
                        Short cycling (frequent start-stop behavior) strains your system, increases wear and energy bills, and could stem from a faulty thermostat.
                        </p>
                    </div>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>4. Slow or Unresponsive Controls</h3>
                        <p>
                        If your thermostat doesn&apos;t react promptly to setting changes, internal electrical issues might be to blame.
                        </p>
                    </div>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>5. Blank Display or Power Failure</h3>
                        <p>
                        A blank screen may signal a power supply issue or internal component failure.
                        </p>
                    </div>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>6. Uneven Room Temperatures</h3>
                        <p>
                        If some rooms are too cold while others are warm, your thermostat might be misreading temperatures or misfiring signals.
                        </p>
                    </div>

                    <div className='space-y-2'>
                        <h3 className='font-bold'>7. Sudden Spikes in Energy Bills</h3>
                        <p>
                        An inefficient thermostat may run the aircon longer than needed, driving up your electricity usage.
                        </p>
                    </div>

                    <p>
                        If you observe these issues, it&apos;s best to contact a certified aircon technician to assess and replace the thermostat if necessary.
                    </p>
                    </section>

                    <section className='py-2 space-y-2'>
                    <h2 className='text-xl font-bold uppercase'>Aircon Thermostat Replacement Cost</h2>
                    <p>
                        Thermostat replacement costs can vary depending on the model, installation complexity, and labor charges. Most replacements include both installation and configuration to ensure seamless integration with your aircon system.
                    </p>
                    <div>
                        As a general estimate:
                        <ul className='list-disc list-inside mt-2'>
                        <li><strong>Thermostat unit:</strong> $100–$250</li>
                        <li><strong>Installation & labor:</strong> $50–$150</li>
                        </ul>
                        Final prices may vary based on location, additional repairs, or advanced thermostat models.
                    </div>
                    </section>
                </div>
                </div>

            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/> 
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}